import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { E } from "@/components/EditableText";
import { submitPortfolioContact } from "@/lib/api";
import { SUN, INK, CREAM } from "@/components/guardian/pricing/config";

const ARCHETYPES = [
  { id: "sponsor-bank", label: "Sponsor bank / BaaS" },
  { id: "law-firm", label: "Law firm" },
  { id: "vc-pe", label: "VC / PE portfolio" },
  { id: "accelerator", label: "Accelerator / studio" },
  { id: "association", label: "Association / network" },
  { id: "other", label: "Other" },
];

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <div className="pcp-mono text-[10px] opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}

export function ContactSection({ portfolioSize, tier, totalManagerMonthly, totalManagerAnnual, engagementPct, seenSections, trackedTotal }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [role, setRole] = useState("");
  const [archetype, setArchetype] = useState("sponsor-bank");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !organization.trim()) {
      toast.error("Name, email, and organization are required.");
      return;
    }
    setBusy(true);
    try {
      const res = await submitPortfolioContact({
        name: name.trim(),
        email: email.trim(),
        organization: organization.trim(),
        role: role.trim() || null,
        archetype,
        portfolio_size: portfolioSize,
        tier: tier?.label || null,
        monthly_estimate: totalManagerMonthly || null,
        annual_estimate: totalManagerAnnual || null,
        notes: notes.trim() || null,
        engagement_pct: engagementPct,
        sections_viewed: seenSections,
      });
      if (res?.ok) {
        setDone(true);
        toast.success("Sent — Colin will reach out shortly.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error("Network error — please email colindarke@complisun.com directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="px-6 md:px-12 py-16 md:py-24" style={{ background: INK, color: CREAM }}>
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1fr] gap-12">
        <div>
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN }}>/ Request a portfolio review</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-6 leading-[1.05]">
            <E k="contact.headline" defaultText="One conversation. We'll size it against your portfolio and send a tailored program design." as="span" multiline />
          </h2>
          <p className="text-base md:text-lg opacity-80 leading-relaxed mb-8">
            <E
              k="contact.body"
              defaultText="Tell us about the portfolio. We'll come back with a concrete program design — modules, hours, pricing, and the regulatory specifics that map to your companies. No spam, no drip — direct from Colin Darke."
              as="span"
              multiline
            />
          </p>
          <ul className="space-y-3 text-sm opacity-80">
            {[
              "Detroit-rooted, attorney-led — we answer the phone.",
              "Already running these relationships for sponsor banks.",
              "Immediate automation. Then iteration and improvement together.",
              "If we're not a fit, we'll say so and refer where it makes sense.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: SUN }} strokeWidth={2} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          {done ? (
            <div className="rounded-xl p-8 text-center" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${SUN}` }}>
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4" style={{ color: SUN }} strokeWidth={1.6} />
              <h3 className="pcp-h text-3xl mb-3">Got it.</h3>
              <p className="opacity-80 mb-4">Colin received your request along with the program sizing from above. Expect a reply within one business day.</p>
              <p className="pcp-mono text-[10px] opacity-60">Sections viewed: {seenSections.length}/{trackedTotal} · Engagement {engagementPct}%</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="rounded-xl p-6 md:p-8" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
              <div className="pcp-mono text-[10px] mb-5 opacity-70">/ Your context will be sent with this message</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" htmlFor="pcp-name">
                  <input
                    id="pcp-name" data-testid="contact-name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full bg-transparent border-b border-cream/30 focus:border-cream py-2 outline-none text-cream"
                  />
                </Field>
                <Field label="Email" htmlFor="pcp-email">
                  <input
                    id="pcp-email" type="email" data-testid="contact-email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-transparent border-b border-cream/30 focus:border-cream py-2 outline-none text-cream"
                  />
                </Field>
                <Field label="Organization" htmlFor="pcp-org">
                  <input
                    id="pcp-org" data-testid="contact-org"
                    value={organization} onChange={(e) => setOrganization(e.target.value)}
                    required
                    className="w-full bg-transparent border-b border-cream/30 focus:border-cream py-2 outline-none text-cream"
                  />
                </Field>
                <Field label="Role (optional)" htmlFor="pcp-role">
                  <input
                    id="pcp-role" data-testid="contact-role"
                    value={role} onChange={(e) => setRole(e.target.value)}
                    placeholder="CCO · Managing Partner · GC"
                    className="w-full bg-transparent border-b border-cream/30 focus:border-cream py-2 outline-none text-cream placeholder:text-cream/40"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <div className="pcp-mono text-[10px] opacity-70 mb-2">/ Archetype</div>
                <div className="flex flex-wrap gap-2">
                  {ARCHETYPES.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setArchetype(a.id)}
                      data-testid={`contact-archetype-${a.id}`}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition border"
                      style={{
                        background: archetype === a.id ? SUN : "transparent",
                        color: archetype === a.id ? INK : CREAM,
                        borderColor: archetype === a.id ? SUN : "rgba(253, 251, 245, 0.25)",
                      }}
                    >{a.label}</button>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="pcp-mono text-[10px] opacity-70 mb-2">/ Notes for Colin (optional)</div>
                <textarea
                  data-testid="contact-notes"
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Portfolio composition, urgency, examiner pressure, what you'd want a pilot to look like…"
                  className="w-full bg-transparent border border-cream/20 focus:border-cream/60 rounded-md p-3 outline-none text-cream placeholder:text-cream/40 text-sm resize-y"
                />
              </div>

              <div className="mt-5 p-4 rounded-md" style={{ background: "rgba(245,180,0,0.10)", border: `1px solid ${SUN}40` }}>
                <div className="pcp-mono text-[9px] mb-2" style={{ color: SUN }}>/ Will be sent with this message</div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div><div className="opacity-60">Portfolio</div><div className="font-semibold">{portfolioSize} co · {tier.label}</div></div>
                  <div><div className="opacity-60">Estimate</div><div className="font-semibold">{totalManagerMonthly ? `$${totalManagerMonthly.toLocaleString()}/mo` : "Custom"}</div></div>
                  <div><div className="opacity-60">ARR</div><div className="font-semibold">{totalManagerAnnual ? `$${totalManagerAnnual.toLocaleString()}` : "—"}</div></div>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md font-medium transition disabled:opacity-50"
                style={{ background: SUN, color: INK }}
              >
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : (<><Send className="h-4 w-4" /> Send to Colin</>)}
              </button>
              <p className="text-[11px] opacity-55 mt-3 text-center">
                Or email <a href="mailto:colindarke@complisun.com" className="underline">colindarke@complisun.com</a> directly.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
